<script setup lang="ts">
import { skillCategories } from '../../data/skills'
import { revealDirective } from '../../composables/useReveal'

defineOptions({ directives: { reveal: revealDirective } })

/*
  Proficiency is shown as a four-step meter instead of a percentage. Nobody's
  React is meaningfully "80%", and precise-looking numbers on a self-assessment
  read as noise — four steps is as much resolution as the claim can carry.
*/
const steps: Record<string, number> = {
  beginner: 1,
  intermediate: 2,
  advanced: 3,
  expert: 4,
}
</script>

<template>
  <div class="skills">
    <section
      v-for="category in skillCategories"
      :key="category.key"
      class="group"
      v-reveal
    >
      <header class="group-head">
        <h3 class="eyebrow">{{ category.name }}</h3>
        <p class="group-desc">{{ category.description }}</p>
      </header>

      <ul class="list">
        <li v-for="skill in category.skills" :key="skill.id" class="skill">
          <span class="name">{{ skill.name }}</span>
          <span
            class="meter"
            :title="skill.proficiency"
            :aria-label="`${skill.name}: ${skill.proficiency}`"
          >
            <i
              v-for="step in 4"
              :key="step"
              :class="{ on: step <= (steps[skill.proficiency] ?? 0) }"
            ></i>
          </span>
          <span v-if="skill.yearsOfExperience" class="years meta">
            {{ skill.yearsOfExperience }}y
          </span>
        </li>
      </ul>
    </section>
  </div>
</template>

<style scoped>
.skills {
  display: grid;
  gap: 0;
  border-top: 1px solid var(--c-rule-strong);
}

.group {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem 2rem;
  padding-block: clamp(1.5rem, 4vw, 2.2rem);
  border-bottom: 1px solid var(--c-rule);
}
@media (min-width: 880px) {
  .group {
    grid-template-columns: 15rem minmax(0, 1fr);
  }
}

.group-head h3 {
  margin: 0 0 0.5rem;
}
.group-desc {
  margin: 0;
  color: var(--c-ink-3);
  font-size: 0.86rem;
  max-width: 30ch;
}

.list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 0;
  grid-template-columns: 1fr;
  align-content: start;
}
@media (min-width: 620px) {
  .list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 2.5rem;
  }
}

.skill {
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  gap: 0.8rem;
  padding: 0.42rem 0;
  border-bottom: 1px solid var(--c-rule);
}
.name {
  color: var(--c-ink);
  font-size: 0.92rem;
}

.meter {
  display: inline-flex;
  gap: 3px;
}
.meter i {
  width: 14px;
  height: 3px;
  background: var(--c-rule);
  border-radius: 1px;
}
.meter i.on {
  background: var(--c-accent);
}

.years {
  color: var(--c-ink-3);
  font-size: 0.66rem;
  min-width: 1.9rem;
  text-align: right;
}
</style>
